import { useMutation } from '@apollo/client';
import { createError } from '@common/neo.tax';
import { AlertModalLayout, Button } from '@ui-components/neo.tax';
import { useState } from 'react';

import {
  RemoveCompanyBulkTicketingDataFileMutation,
  RemoveCompanyBulkTicketingDataFileMutationVariables,
  REMOVE_COMPANY_BULK_TICKETING_DATA_FILE,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyOrFail } from '@neo.tax/shared/hooks';
import { DataSourcesTableItem } from '@neo.tax/shared/utils';

import { GeneralFormErrorStyled, ModalContainer } from './delete-ticketing-file-upload.styled';

interface DeleteTicketingFileUploadProps {
  itemToDelete: DataSourcesTableItem;
  close: () => void;
}

export const DeleteTicketingFileUpload = ({
  close,
  itemToDelete,
}: DeleteTicketingFileUploadProps): React.ReactElement | null => {
  if (itemToDelete.itemType !== 'ticketing-file') {
    throw createError('DeleteTicketingFileUploadError', 'This component can only be used to delete ticketing file.');
  }
  const fileUploadId = itemToDelete.id;
  // The file holds one ticketing entity type, and the row already carries its label ("Users",
  // "Tickets"); naming it is what tells the user which of their uploads they are about to lose.
  const entityLabel = itemToDelete.entity?.toLowerCase() ?? 'records';
  const company = useCurrentCompanyOrFail();

  const [removeCompanyBulkTicketingDataFile, { loading }] = useMutation<
    RemoveCompanyBulkTicketingDataFileMutation,
    RemoveCompanyBulkTicketingDataFileMutationVariables
  >(REMOVE_COMPANY_BULK_TICKETING_DATA_FILE);

  const [errorMessage, setErrorMessage] = useState<string>();

  const onSubmit = async (): Promise<void> => {
    try {
      await removeCompanyBulkTicketingDataFile({
        variables: {
          fileUploadId,
        },
        optimisticResponse: {
          removeCompanyBulkTicketingDataFile: {
            __typename: 'Company',
            id: company.id,
            bulkTicketingDataUploads: company.bulkTicketingDataUploads.filter((upload) => upload.id !== fileUploadId),
          },
        },
      });
      close();
    } catch {
      // TODO once Taylor's error handling refactor is in place, we can try to parse the error and show a more specific message
      // https://github.com/neo-tax/platform-new/pull/2913
      setErrorMessage('Something unexpected prevented the account from being deleted. Please try again.');
    }
  };

  return (
    <ModalContainer>
      <AlertModalLayout
        title={`Delete ${itemToDelete.title}`}
        copy={`This will permanently remove all ${entityLabel} from this data source and all allocations associated with those ${entityLabel}. Are you sure you want to delete ${itemToDelete.title}?`}
        trailingContent={
          <>
            <Button size="base" variant="white" rounded="regular" onClick={close}>
              Cancel
            </Button>
            <Button disabled={loading} rounded="regular" size="base" type="submit" variant="rsbry" onClick={onSubmit}>
              {loading ? 'Deleting...' : 'Confirm'}
            </Button>
          </>
        }
      >
        <GeneralFormErrorStyled centered error={!!errorMessage}>
          {errorMessage}
        </GeneralFormErrorStyled>
      </AlertModalLayout>
    </ModalContainer>
  );
};
